import { observer } from "mobx-react";
import type React from "react";
import { useEffect } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { TeacherModals } from "constants/modals-constants";
import { MISC_PAGE_ROUTES, TEACHER_HOME_PAGE_ROUTES } from "constants/routes";

import { copyTextToClipboard } from "_helpers/clipboard";
import { buildUrlWithPathParams } from "_helpers/utils/urlBuilder";

import { useDomainHandler } from "hooks/useDomainHandler";
import { useGroups } from "hooks/useGroups";
import { useModal } from "hooks/useModal";
import { useRedirect } from "hooks/useRedirect";

import { Breadcrumbs } from "components/Breadcrumbs";
import { InfoMessage } from "components/InfoMessage/InfoMessage";
import { LoadingSpinner } from "components/LoadingSpinner";
import { Note } from "components/Note/Note";

import { Heading2 } from "styles/elements/Headings";
import { ParagraphLarge, ParagraphSmall } from "styles/elements/Texts";
import { CssButton, CssFlex, CssGrid, CssIcon } from "styles/helpers/layout";

import { StyledButton, StyledShareableLink, StyledStudentIcon } from "./GroupDetails.styled";
import { AddStudentsToGroupModal } from "./components/AddStudentsToGroupModal/AddStudentsToGroupModal";
import { ConfirmGroupDeletionModal } from "./components/GroupDeletionModal/GroupDeletionModal";
import { GroupMembers } from "./components/GroupMembers/GroupMembers";
import { RenameGroupModal } from "./components/RenameGroupModal/RenameGroupModal";

export const GroupDetailsPage: React.FC = observer(() => {
  const { t } = useTranslation("groups");
  const { redirectToUrl } = useRedirect();
  const { groupId } = useParams() as unknown as { groupId: string };

  const { openModal } = useModal();
  const { getStudentTermByDomain } = useDomainHandler();
  const {
    getGroup,
    loading,
    currentGroup,
    setCurrentGroup,
    notAddedStudents,
    setNotAddedStudents,
  } = useGroups();

  useEffect(() => {
    getGroup(Number.parseInt(groupId, 10));
    return () => {
      setCurrentGroup(null);
      setNotAddedStudents([]);
    };
  }, [groupId]);

  if (loading) {
    return <LoadingSpinner />;
  }

  // If group is not found, show error message
  if (!currentGroup) {
    return (
      <InfoMessage
        dataCy="group-not-found-message"
        title={t("groupDetails.groupNotFoundMessage.title")}
        icon="fal fa-users-slash"
      >
        <ParagraphLarge marginBottom={4}>
          {t("groupDetails.groupNotFoundMessage.description")}
        </ParagraphLarge>
        <CssButton
          dataCy="return-to-group-list-button"
          variant="primary"
          onClick={() => redirectToUrl(TEACHER_HOME_PAGE_ROUTES.GROUPS)}
        >
          {t("groupDetails.groupNotFoundMessage.returnButton")}
        </CssButton>
      </InfoMessage>
    );
  }

  const joinGroupLink =
    window.location.origin + buildUrlWithPathParams(MISC_PAGE_ROUTES.JOIN_GROUP, { groupId });

  return (
    <CssFlex flexDirection="column" gap={2}>
      {/* -- Breadcrumbs -- */}
      <Breadcrumbs
        divider="/"
        excludeRoutes={[
          "/",
          TEACHER_HOME_PAGE_ROUTES.HOME_PAGE,
          TEACHER_HOME_PAGE_ROUTES.EDIT_GROUP.replace(":groupId", ""),
        ]}
        routes={[
          {
            path: TEACHER_HOME_PAGE_ROUTES.GROUPS,
            breadcrumb: t("groups:groupDetails.breadcrumbs.groupList"),
          },
          { path: TEACHER_HOME_PAGE_ROUTES.EDIT_GROUP, breadcrumb: currentGroup.name },
        ]}
      />

      {/* -- Heading -- */}
      <CssFlex flexDirection="row" gap={2} alignItems="flex-start" justifyContent="space-between">
        <CssFlex alignItems="center" gap={1}>
          <Heading2 data-cy="group-name" marginBottom="0.5rem">
            {currentGroup.name}
          </Heading2>
          <CssButton
            aria-label={t("groups:groupDetails.groupActionButtons.editGroupName")}
            data-cy="edit-group-button"
            onClick={() => openModal(TeacherModals.RenameGroup)}
          >
            <CssIcon iconName="fas fa-pencil" iconSize="lg" />
          </CssButton>
        </CssFlex>
        <CssFlex gap={1}>
          <StyledButton
            variant="error"
            data-cy="delete-group-button"
            onClick={() => openModal(TeacherModals.ConfirmGroupDeletion)}
          >
            <CssIcon iconName="fas fa-trash" />
            {t("groups:groupDetails.groupActionButtons.delete")}
          </StyledButton>
          <StyledButton
            variant="primary"
            data-cy="add-students-button"
            onClick={() => openModal(TeacherModals.AddStudentsToGroup)}
          >
            <CssIcon iconName="far fa-plus" />
            {t("groups:groupDetails.groupActionButtons.addStudent", {
              studentTerm: getStudentTermByDomain({ usePlural: true }),
            })}
          </StyledButton>
        </CssFlex>
      </CssFlex>

      <CssFlex alignItems="center" gap={0.5} marginTop="-2rem">
        <StyledStudentIcon iconName="fas fa-users" iconSize="xs" />
        <ParagraphSmall>
          <Trans
            components={{ countContainerTag: <span data-cy="group-student-count" /> }}
            i18nKey="groups:groupDetails.studentCount"
            t={t}
            values={{
              studentCount: currentGroup.students.length,
              studentTerm: getStudentTermByDomain({
                usePlural: currentGroup.students.length !== 1,
              }),
            }}
          />
        </ParagraphSmall>
      </CssFlex>

      {/* -- Add group members error -- */}
      {notAddedStudents.length > 0 && (
        <Note dataCy="add-students-error" variant="error">
          <Trans
            i18nKey="groups:groupDetails.failToAddSomeStudentsMessage"
            t={t}
            values={{
              studentTerm: getStudentTermByDomain({
                usePlural: true,
              }),
            }}
          />
          <ul data-cy="not-added-student-list">
            {notAddedStudents.map((studentEmail) => (
              <li key={studentEmail}>{studentEmail}</li>
            ))}
          </ul>
        </Note>
      )}

      {/* -- Group shareable link -- */}
      <CssGrid gridTemplateColumns="1fr auto" gap={1}>
        <StyledShareableLink
          aria-label={t("groups:groupDetails.shareableLink.label")}
          data-cy="shareable-group-link-input"
          name="join-group-url"
          readOnly
          disabled
          type="text"
          value={joinGroupLink}
        />
        <StyledButton
          data-cy="copy-shareable-group-link"
          variant="brand"
          onClick={() => copyTextToClipboard(joinGroupLink)}
        >
          <CssIcon iconName="fas fa-copy" /> {t("groups:groupDetails.shareableLink.copyButton")}
        </StyledButton>
      </CssGrid>

      <Note variant="info">
        {t("groups:groupDetails.shareableLink.additionalInfo", {
          studentTerm: getStudentTermByDomain({ usePlural: true }),
        })}
      </Note>

      {/* -- Student list -- */}
      {currentGroup.students.length > 0 ? (
        <GroupMembers students={currentGroup.students} groupId={+groupId} />
      ) : (
        <InfoMessage
          dataCy="empty-group-message"
          title={t("groups:groupDetails.emptyGroupMessage.title")}
          icon="fal fa-users-slash"
        >
          <ParagraphLarge>
            {t("groups:groupDetails.emptyGroupMessage.description", {
              studentTerm: getStudentTermByDomain({ usePlural: true }),
            })}
          </ParagraphLarge>
        </InfoMessage>
      )}

      {/* -- Action modals -- */}
      <ConfirmGroupDeletionModal groupId={+groupId} groupName={currentGroup.name} />
      <RenameGroupModal groupId={+groupId} groupName={currentGroup.name} />
      <AddStudentsToGroupModal groupId={+groupId} groupName={currentGroup.name} />
    </CssFlex>
  );
});
